import clsx from 'clsx';
import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import EmailLeadCapture from '@components/EmailLeadCapture';
import useEventTracking from '@hooks/useEventTracking';
import useSubscribeToNewsletter from '@hooks/useSubscribeToNewsletter';
import type { Newsletter } from '@interfaces/homepage';
import { newsletter_list_path } from '@routes';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { FormLocation, LeadTypes } from '@utils/enums';

import NewsletterSignupConfirmationCard from './NewsletterSignupConfirmationCard';
import { SEE_ALL_NEWSLETTERS_TEXT } from './constants';

interface NewsletterSignupCardProps {
  className?: string;
  ctaText?: string;
  isHorizontal?: boolean;
  leadType: LeadTypes;
  newsletter: Newsletter;
  redirectToOnSuccess: string;
  splitOnTablet?: boolean;
}

const NewsletterSignupCard = ({
  className,
  ctaText,
  isHorizontal = false,
  leadType,
  newsletter: { description, id: newsletterId, isLoggedIn, isSubscribed, name },
  redirectToOnSuccess,
  splitOnTablet = false,
}: NewsletterSignupCardProps) => {
  const { trackEvent, Track } = useEventTracking({
    location: TrackingLocation.TITVNewsletterSignup,
  });

  const [successfullySubscribed, setSuccessfullySubscribed] = useState(false);
  const { mutate: subscribeToNewsletter, isLoading: isSubscribeLoading } =
    useSubscribeToNewsletter();

  const notSubscribed = !isSubscribed && !successfullySubscribed;

  const trackNewsletterLinkClick = (clickText: string) =>
    trackEvent(click({ clickTarget: ClickTarget.ViewAllNewsletters, clickText }));

  const handleSubscribe = () =>
    subscribeToNewsletter(
      { newsletter_id: newsletterId },
      {
        onSuccess: () => {
          setSuccessfullySubscribed(true);
          toast.success(`Thank you for signing up for ${name}.`);
        },
        onError: () => {
          toast.error(`There was a problem signing you up for ${name}, please try again later`);
        },
      }
    );

  if ((isSubscribed || successfullySubscribed) && !isHorizontal) {
    return (
      <Track>
        <NewsletterSignupConfirmationCard handleClickCapture={trackNewsletterLinkClick} />
      </Track>
    );
  }

  return (
    <Track>
      <aside
        className={clsx(className, 'border-0 border-t border-solid border-primary-black')}
        data-testid="titv-rail-newsletter-signup"
      >
        <div
          className={clsx('flex flex-col', {
            'sm:max-md:flex-row sm:max-md:items-start sm:max-md:gap-32': splitOnTablet,
            'w-full sm:flex-row sm:gap-36 lg:mx-auto lg:max-w-[900px] lg:gap-32': isHorizontal,
          })}
        >
          <div className={clsx({ 'sm:max-md:w-1/2': splitOnTablet, 'w-full': isHorizontal })}>
            <h2
              className={clsx('mb-0 font-suisse-neue text-primary-black lg:text-subheadline-sm', {
                'mt-0 text-subheadline-sm': isHorizontal,
                'mt-32 text-body-md': !isHorizontal,
              })}
            >
              {ctaText && notSubscribed ? `${ctaText} ` : ''}
              {name} Newsletter
            </h2>
            <p className="my-10 font-suisse-screen text-disclosure-md text-primary-black">
              {description}
            </p>
          </div>
          <div
            className={clsx('flex flex-col', {
              'sm:max-md:mt-32 sm:max-md:w-1/2': splitOnTablet,
              'mt-20 lg:mt-0': isHorizontal,
            })}
          >
            {isLoggedIn && notSubscribed ? (
              <PrimaryButton
                as="button"
                className="w-full px-24 py-[9px]"
                disabled={isSubscribeLoading}
                onClick={handleSubscribe}
              >
                Sign up
              </PrimaryButton>
            ) : notSubscribed ? (
              <EmailLeadCapture
                buttonText="Sign up"
                buttonClassName="w-full"
                formClassName={clsx('*:flex-col *:gap-8 *:*:*:*:!border-grayscale-gray', {
                  'sm:min-w-[258px]': isHorizontal,
                })}
                formLocation={FormLocation.TITV}
                gdprClassName="md:items-start flex-col lg:flex-row lg:items-center mb-8"
                inputClassName="w-full dark:bg-[var(--light-grayscale-charcoal)]"
                leadType={leadType}
                newsletterIds={[Number(newsletterId)]}
                redirectToOnSuccess={redirectToOnSuccess}
                toastMessageOnSuccess={`Thank you for signing up for ${name}.`}
              />
            ) : (
              <NewsletterSignupConfirmationCard
                className="*:mt-0 *:w-max"
                handleClickCapture={trackNewsletterLinkClick}
              />
            )}
            {notSubscribed && (
              <TextButton
                as="link"
                className="mt-16 w-fit max-sm:!w-fit"
                href={newsletter_list_path()}
                size="small"
                onClickCapture={() => trackNewsletterLinkClick(SEE_ALL_NEWSLETTERS_TEXT)}
              >
                {SEE_ALL_NEWSLETTERS_TEXT}
              </TextButton>
            )}
          </div>
        </div>
      </aside>
    </Track>
  );
};

export default NewsletterSignupCard;
